import {
  TABLE_SETTINGS_KEYS,
  BREAKPOINT_WIDTH_KEYS,
  COLUMN_SETTINGS_KEYS,
  ROW_SETTINGS_KEYS,
} from "./tableConstants";

/**
 * Check if an object is a breakpoint object
 */
const BREAKPOINT_KEYS = new Set(['xs', 'sm', 'md', 'lg', 'xl']);
const isBreakpointObject = (value: any): boolean => {
  if (typeof value !== 'object' || !value) return false;
  return BREAKPOINT_KEYS.has(Object.keys(value)[0] || '');
};

/**
 * Compact border sides into a string
 */
const BORDER_SIDES = ['top', 'right', 'bottom', 'left'] as const;
const BORDER_SIDE_PREFIXES: Record<string, string> = { top: 't', right: 'r', bottom: 'b', left: 'l' };

const compactBorder = (value: any): string => {
  const parts: string[] = [];
  BORDER_SIDES.forEach(side => {
    if (value[side]?.color || value[side]?.width) {
      parts.push(`${BORDER_SIDE_PREFIXES[side]}:${value[side]?.color || ''}:${value[side]?.width || ''}`);
    }
  });
  return parts.join(';');
};

/**
 * Compact an object using key mappings
 */
const compactObject = (obj: Record<string, any>, keyMap: Record<string, string>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === '') continue;
    const compactKey = keyMap[key] || key;
    
    if (typeof value === 'object' && !Array.isArray(value)) {
      if (isBreakpointObject(value)) {
        // It's a breakpoint styles object - compact each breakpoint
        const compactedBp: Record<string, any> = {};
        for (const [bp, bpValue] of Object.entries(value)) {
          if (bpValue && typeof bpValue === 'object') {
            compactedBp[bp] = compactObject(bpValue as Record<string, any>, { ...BREAKPOINT_WIDTH_KEYS, ...COLUMN_SETTINGS_KEYS });
          } else {
            compactedBp[bp] = bpValue;
          }
        }
        result[compactKey] = compactedBp;
      } else if (key === 'padding' && value.top !== undefined) {
        // Convert padding object to compact string "t,r,b,l"
        result[compactKey] = `${value.top},${value.right},${value.bottom},${value.left}`;
      } else if (key === 'border' && (value.top || value.right || value.bottom || value.left)) {
        // Convert border object to compact string "t:color:width;r:color:width;..."
        const compacted = compactBorder(value);
        if (compacted) result[compactKey] = compacted;
      } else {
        result[compactKey] = value;
      }
    } else {
      result[compactKey] = value;
    }
  }
  return result;
};

/**
 * Expand border string to object
 */
const BORDER_PREFIX_TO_SIDE: Record<string, 'top' | 'right' | 'bottom' | 'left'> = {
  t: 'top',
  r: 'right', 
  b: 'bottom',
  l: 'left',
};

const expandBorder = (value: string): any => {
  const border: any = { top: {}, right: {}, bottom: {}, left: {} };
  value.split(';').forEach((part: string) => {
    const [prefix, color, width] = part.split(':');
    const side = BORDER_PREFIX_TO_SIDE[prefix];
    if (side) {
      border[side] = { color, width };
    }
  });
  return border;
};

/**
 * Expand a compacted object back to full keys
 */
const expandObject = (obj: Record<string, any>, keyMap: Record<string, string>): Record<string, any> => {
  const reverseMap: Record<string, string> = {};
  for (const [full, compact] of Object.entries(keyMap)) {
    reverseMap[compact] = full;
  }
  
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = reverseMap[key] || key;
    
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      if (isBreakpointObject(value)) {
        const expandedBp: Record<string, any> = {};
        for (const [bp, bpValue] of Object.entries(value)) {
          if (bpValue && typeof bpValue === 'object') {
            expandedBp[bp] = expandObject(bpValue as Record<string, any>, { ...BREAKPOINT_WIDTH_KEYS, ...COLUMN_SETTINGS_KEYS });
          } else {
            expandedBp[bp] = bpValue;
          }
        }
        result[fullKey] = expandedBp;
      } else {
        result[fullKey] = value;
      }
    } else if (typeof value === 'string') {
      // Check if it's a compact padding string "t,r,b,l"
      if (fullKey === 'padding' && value.includes(',')) {
        const [top, right, bottom, left] = value.split(',');
        result[fullKey] = { top, right, bottom, left };
      }
      // Check if it's a compact border string "t:color:width;..."
      else if (fullKey === 'border' && value.includes(':')) {
        result[fullKey] = expandBorder(value);
      } else {
        result[fullKey] = value;
      }
    } else {
      result[fullKey] = value;
    }
  }
  return result;
};

/**
 * Compact table settings for efficient storage
 */
export const compactTableSettings = (settings: Record<string, any>): Record<string, any> => {
  return compactObject(settings, TABLE_SETTINGS_KEYS);
};

/**
 * Expand compact table settings back to full format
 */
export const expandTableSettings = (compact: Record<string, any>): Record<string, any> => {
  return expandObject(compact, TABLE_SETTINGS_KEYS);
};

/**
 * Compact column settings for efficient storage
 */
export const compactColumnSettings = (settings: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [colIdx, colSettings] of Object.entries(settings)) {
    result[colIdx] = compactObject(colSettings as Record<string, any>, COLUMN_SETTINGS_KEYS);
  }
  return result;
};

/**
 * Expand compact column settings back to full format
 */
export const expandColumnSettings = (compact: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [colIdx, colSettings] of Object.entries(compact)) {
    result[colIdx] = expandObject(colSettings as Record<string, any>, COLUMN_SETTINGS_KEYS);
  }
  return result;
};

